import { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import DesignSystem from '../../designSystem';
import { useT } from '../../i18n/useT';
import { setAcceptedPrivacyPolicy } from '../../utils/privacyConsent';

const POLICY_IFRAME_HEIGHT = 3200;

type PrivacyPolicyConsentDialogProps = {
  open: boolean;
  onClose: () => void;
  onAccepted: () => void;
};

/**
 * Scroll-to-accept privacy gate — parity with Android `PrivacyPolicyActivity`
 * (`bAccept` enabled only after `ScrollView` reaches bottom).
 */
export default function PrivacyPolicyConsentDialog({
  open,
  onClose,
  onAccepted,
}: PrivacyPolicyConsentDialogProps) {
  const t = useT();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canAccept, setCanAccept] = useState(false);
  const backendUrl = process.env.REACT_APP_API_URL || window.location.origin;

  const checkScrollBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
    if (atBottom) setCanAccept(true);
  }, []);

  const handleAccept = () => {
    setAcceptedPrivacyPolicy(true);
    onAccepted();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: DesignSystem.borderRadius.lg } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>{t('privacyPolicyTitle')}</DialogTitle>
      <DialogContent sx={{ px: 2, pt: 0 }}>
        <Typography variant="body2" sx={{ color: DesignSystem.colors.textSecondary, mb: 1.5 }}>
          {t('privacyPolicyScrollHint')}
        </Typography>
        <Box
          ref={scrollRef}
          onScroll={checkScrollBottom}
          sx={{
            maxHeight: 360,
            overflow: 'auto',
            border: `1px solid ${DesignSystem.colors.border}`,
            borderRadius: DesignSystem.borderRadius.md,
            backgroundColor: DesignSystem.colors.surface,
          }}
        >
          <Box
            component="iframe"
            title={t('privacyPolicyTitle')}
            src={`${backendUrl}/privacypolicy`}
            sx={{
              display: 'block',
              width: '100%',
              height: POLICY_IFRAME_HEIGHT,
              border: 0,
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, flexDirection: 'column', gap: 1 }}>
        <Button
          fullWidth
          variant="contained"
          disabled={!canAccept}
          onClick={handleAccept}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: DesignSystem.borderRadius.xl,
            py: 1.25,
          }}
        >
          {t('privacyPolicyAccept')}
        </Button>
        <Button fullWidth onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
